import { useMemo, useState } from "react";
import { useSite } from "@/context/SiteContext";
import { mediaUrl, openWhatsApp } from "@/lib/api";
import { Reveal, SectionHead } from "@/components/Reveal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

function SampleCard({ s, i, settings }) {
  const select = () => {
    const tpl = settings?.templates?.design_selection || "Hi! I selected the {design} design.";
    openWhatsApp(settings?.contact?.whatsapp || "", tpl.replace("{design}", s.title), { trackPath: "/select-design" });
  };
  return (
    <Reveal delay={(i % 3) * 0.06}>
      <div className="glass rounded-3xl overflow-hidden group h-full flex flex-col" data-testid={`samples-page-card-${i}`}>
        <div className="relative aspect-[4/3] overflow-hidden">
          <img src={mediaUrl(s.image_url)} alt={s.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          {s.badge && <span className="absolute top-3 left-3 font-mono-label text-[8px] bg-accent text-white rounded-full px-2.5 py-1">{s.badge}</span>}
          <span className="absolute top-3 right-3 glass-strong rounded-full px-3 py-1 text-[10px] font-mono-label text-mist">{s.category}</span>
        </div>
        <div className="p-5 flex-1 flex flex-col">
          <div className="flex items-center justify-between gap-2 mb-4">
            <h3 className="font-display text-base font-semibold text-white">{s.title}</h3>
            <span className="font-display text-sm font-bold text-accent-bright">{s.price}</span>
          </div>
          <div className="mt-auto flex gap-2">
            <Dialog>
              <DialogTrigger asChild>
                <button className="flex-1 text-xs font-semibold px-3 py-2.5 rounded-xl border border-white/15 text-mist hover:text-white hover:border-accent/50 transition-colors" data-testid={`samples-preview-btn-${i}`}>
                  Preview
                </button>
              </DialogTrigger>
              <DialogContent className="glass-strong border-white/10 max-w-3xl p-2 sm:p-3" data-testid="samples-preview-dialog">
                <DialogHeader className="px-4 pt-3 pb-2">
                  <DialogTitle className="font-display text-white text-base text-left">{s.title}</DialogTitle>
                </DialogHeader>
                <div className="spot-frame aspect-[16/10]">
                  <img src={mediaUrl(s.image_url)} alt={s.title} />
                </div>
              </DialogContent>
            </Dialog>
            <button onClick={select} className="flex-1 text-xs font-semibold px-3 py-2.5 rounded-xl bg-accent hover:bg-accent-bright text-white transition-colors" data-testid={`samples-select-btn-${i}`}>
              Select Design
            </button>
          </div>
        </div>
      </div>
    </Reveal>
  );
}

export default function Samples() {
  const { settings, samples } = useSite();
  const [filter, setFilter] = useState("All");

  const categories = useMemo(() => {
    const set = new Set((samples || []).map((s) => s.category).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [samples]);

  const list = filter === "All" ? samples || [] : (samples || []).filter((s) => s.category === filter);

  return (
    <div className="pt-24 sm:pt-28" data-testid="samples-page">
      <section className="relative py-14 sm:py-20 overflow-hidden">
        <div className="ambient-glow w-[560px] h-[300px] -top-32 left-0 opacity-40" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead
            eyebrow="The Gallery"
            title="Ready-to-launch website packages"
            sub="Every design is a proven conversion structure — pick one, we customise it to your brand and launch in days."
            align="center"
          />
          <Reveal className="flex flex-wrap justify-center gap-2.5 mb-12">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`px-5 py-2.5 rounded-full text-xs font-semibold border transition-all duration-300 ${filter === c ? "bg-accent border-accent text-white shadow-[0_0_20px_rgba(124,58,237,0.4)]" : "border-white/15 text-mist hover:text-white hover:border-accent/50"}`}
                data-testid={`samples-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {c}
              </button>
            ))}
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {list.map((s, i) => (
              <SampleCard key={s.id || i} s={s} i={i} settings={settings} />
            ))}
          </div>
          {list.length === 0 && (
            <p className="text-center text-sm text-subtle py-16" data-testid="samples-empty-state">No samples in this category yet — check back soon.</p>
          )}
        </div>
      </section>
    </div>
  );
}
